import { Button, Input, Select } from '@/components/ui';
import type { DbModelDefinition, DbModelField } from '@/services/dbManager.service';
import {
  NULL_FILTER_VALUE,
  createEmptyFilterGroup,
  createEmptyFilterRule,
  filterSummary,
  type FilterGroup,
  type FilterNode,
  type FilterOperator,
  type FilterRule,
} from '@/types/dbFilters';
import { Filter, GripVertical, Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';

interface FilterBuilderProps {
  value: FilterGroup;
  onChange: (next: FilterGroup) => void;
  modelDefinition?: DbModelDefinition | null;
  enums?: Record<string, string[]>;
  onApply: () => void;
  onClear: () => void;
}

const STRING_OPERATORS: FilterOperator[] = ['eq', 'neq', 'like', 'ilike', 'notLike', 'notILike', 'in', 'notIn', 'is', 'isNot'];
const NUMBER_OPERATORS: FilterOperator[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'notIn', 'is', 'isNot'];
const BOOLEAN_OPERATORS: FilterOperator[] = ['eq', 'neq', 'is', 'isNot'];
const ENUM_OPERATORS: FilterOperator[] = ['eq', 'neq', 'in', 'notIn', 'is', 'isNot'];
const DATETIME_OPERATORS: FilterOperator[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is', 'isNot'];

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: '=',
  neq: '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  like: 'like',
  ilike: 'ilike',
  notLike: 'not like',
  notILike: 'not ilike',
  in: 'in',
  notIn: 'not in',
  is: 'is',
  isNot: 'is not',
};

function isNumericType(type: string): boolean {
  return ['Int', 'BigInt', 'Float', 'Decimal'].includes(type);
}

function getOperatorOptions(fieldMeta?: DbModelField): Array<{ value: string; label: string }> {
  const operators = !fieldMeta
    ? STRING_OPERATORS
    : fieldMeta.kind === 'enum'
      ? ENUM_OPERATORS
      : fieldMeta.type === 'Boolean'
        ? BOOLEAN_OPERATORS
        : fieldMeta.type === 'DateTime'
          ? DATETIME_OPERATORS
          : isNumericType(fieldMeta.type)
            ? NUMBER_OPERATORS
            : STRING_OPERATORS;

  return operators.map((operator) => ({ value: operator, label: OPERATOR_LABELS[operator] }));
}

function updateNode(root: FilterGroup, nodeId: string, updater: (node: FilterNode) => FilterNode): FilterGroup {
  return {
    ...root,
    children: root.children.map((child) => {
      if (child.id === nodeId) return updater(child);
      if (child.type === 'group') return updateNode(child, nodeId, updater);
      return child;
    }),
  };
}

function updateGroup(root: FilterGroup, groupId: string, updater: (group: FilterGroup) => FilterGroup): FilterGroup {
  if (root.id === groupId) return updater(root);

  return {
    ...root,
    children: root.children.map((child) => {
      if (child.type !== 'group') return child;
      return updateGroup(child, groupId, updater);
    }),
  };
}

function removeNode(root: FilterGroup, nodeId: string): FilterGroup {
  return {
    ...root,
    children: root.children
      .filter((child) => child.id !== nodeId)
      .map((child) => {
        if (child.type !== 'group') return child;
        return removeNode(child, nodeId);
      }),
  };
}

function getFieldFromNode(node: FilterRule, fields: DbModelField[]): DbModelField | undefined {
  return fields.find((field) => field.name === node.field);
}

export function FilterBuilder({ value, onChange, modelDefinition, enums = {}, onApply, onClear }: FilterBuilderProps) {
  const scalarFields = useMemo(
    () =>
      (modelDefinition?.fields ?? [])
        .filter((field) => !field.isArray && field.kind !== 'object')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [modelDefinition]
  );

  const fieldOptions = useMemo(
    () => scalarFields.map((field) => ({ value: field.name, label: `${field.name} (${field.type})` })),
    [scalarFields]
  );

  const defaultField = fieldOptions[0]?.value ?? '';

  const addRuleToGroup = (groupId: string) => {
    const next = updateGroup(value, groupId, (group) => ({
      ...group,
      children: [...group.children, createEmptyFilterRule(defaultField)],
    }));
    onChange(next);
  };

  const addGroupToGroup = (groupId: string) => {
    const next = updateGroup(value, groupId, (group) => ({
      ...group,
      children: [...group.children, createEmptyFilterGroup('AND')],
    }));
    onChange(next);
  };

  const updateRule = (ruleId: string, patch: Partial<FilterRule>) => {
    const next = updateNode(value, ruleId, (node) => {
      if (node.type !== 'rule') return node;
      return {
        ...node,
        ...patch,
      };
    });

    onChange(next);
  };

  const updateGroupLogic = (groupId: string, logic: 'AND' | 'OR') => {
    const next = updateGroup(value, groupId, (group) => ({ ...group, logic }));
    onChange(next);
  };

  const handleRemoveNode = (nodeId: string) => {
    onChange(removeNode(value, nodeId));
  };

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="inline-flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">
            <Filter className="h-3 w-3" />
            {filterSummary(value)}
          </div>
        </div>

        {!modelDefinition ? (
          <div className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">
            Selecciona un esquema para crear filtros.
          </div>
        ) : (
          <FilterGroupNode
            group={value}
            fieldOptions={fieldOptions}
            scalarFields={scalarFields}
            enums={enums}
            isRoot
            onAddGroup={addGroupToGroup}
            onAddRule={addRuleToGroup}
            onGroupLogicChange={updateGroupLogic}
            onRemoveNode={handleRemoveNode}
            onRuleChange={updateRule}
          />
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClear}>
          Clear Filters
        </Button>
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={onApply}>
          Apply Filters
        </Button>
      </div>
    </div>
  );
}

interface FilterGroupNodeProps {
  group: FilterGroup;
  fieldOptions: Array<{ value: string; label: string }>;
  scalarFields: DbModelField[];
  enums: Record<string, string[]>;
  isRoot?: boolean;
  onGroupLogicChange: (groupId: string, logic: 'AND' | 'OR') => void;
  onAddRule: (groupId: string) => void;
  onAddGroup: (groupId: string) => void;
  onRuleChange: (ruleId: string, patch: Partial<FilterRule>) => void;
  onRemoveNode: (nodeId: string) => void;
}

function FilterGroupNode({
  group,
  fieldOptions,
  scalarFields,
  enums,
  isRoot = false,
  onGroupLogicChange,
  onAddRule,
  onAddGroup,
  onRuleChange,
  onRemoveNode,
}: FilterGroupNodeProps) {
  return (
    <div
      className={`space-y-2 rounded-lg border ${isRoot ? 'border-gray-200 bg-gray-50/40 p-3' : 'border-indigo-100 bg-indigo-50/30 p-3'}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {group.children.length >= 2 && (
          <Select
            classNameDiv="w-24"
            value={group.logic}
            onChange={(event) => onGroupLogicChange(group.id, event.target.value as 'AND' | 'OR')}
            options={[
              { value: 'AND', label: 'AND' },
              { value: 'OR', label: 'OR' },
            ]}
            className="h-9 py-1.5 text-xs font-bold"
          />
        )}

        <Button size="sm" variant="secondary" onClick={() => onAddRule(group.id)}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Add Filter
        </Button>

        <Button size="sm" variant="secondary" onClick={() => onAddGroup(group.id)}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Add Group
        </Button>

        {!isRoot && (
          <Button
            size="sm"
            variant="ghost"
            className="text-red-500 hover:bg-red-50 hover:text-red-600"
            onClick={() => onRemoveNode(group.id)}
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove Group
          </Button>
        )}
      </div>

      {group.children.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 bg-white px-3 py-4 text-xs text-gray-400">
          No conditions in this group.
        </div>
      ) : (
        <div className="space-y-2">
          {group.children.map((child) =>
            child.type === 'group' ? (
              <FilterGroupNode
                key={child.id}
                group={child}
                fieldOptions={fieldOptions}
                scalarFields={scalarFields}
                enums={enums}
                onAddGroup={onAddGroup}
                onAddRule={onAddRule}
                onGroupLogicChange={onGroupLogicChange}
                onRemoveNode={onRemoveNode}
                onRuleChange={onRuleChange}
              />
            ) : (
              <FilterRuleRow
                key={child.id}
                rule={child}
                fieldOptions={fieldOptions}
                scalarFields={scalarFields}
                enums={enums}
                onRuleChange={onRuleChange}
                onRemoveNode={onRemoveNode}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}

interface FilterRuleRowProps {
  rule: FilterRule;
  fieldOptions: Array<{ value: string; label: string }>;
  scalarFields: DbModelField[];
  enums: Record<string, string[]>;
  onRuleChange: (ruleId: string, patch: Partial<FilterRule>) => void;
  onRemoveNode: (nodeId: string) => void;
}

function FilterRuleRow({ rule, fieldOptions, scalarFields, enums, onRuleChange, onRemoveNode }: FilterRuleRowProps) {
  const fieldMeta = getFieldFromNode(rule, scalarFields);
  const operatorOptions = getOperatorOptions(fieldMeta);

  const handleFieldChange = (nextField: string) => {
    const nextFieldMeta = scalarFields.find((field) => field.name === nextField);
    const nextOperator = (getOperatorOptions(nextFieldMeta)[0]?.value as FilterOperator | undefined) ?? 'eq';

    onRuleChange(rule.id, {
      field: nextField,
      operator: nextOperator,
      value: nextOperator === 'is' || nextOperator === 'isNot' ? NULL_FILTER_VALUE : '',
    });
  };

  const handleOperatorChange = (nextOperator: FilterOperator) => {
    onRuleChange(rule.id, {
      operator: nextOperator,
      value: nextOperator === 'is' || nextOperator === 'isNot' ? NULL_FILTER_VALUE : rule.value === NULL_FILTER_VALUE ? '' : rule.value,
    });
  };

  const operatorNeedsListValue = rule.operator === 'in' || rule.operator === 'notIn';
  const operatorIsNullStyle = rule.operator === 'is' || rule.operator === 'isNot';

  const enumOptions = fieldMeta && fieldMeta.kind === 'enum' ? (enums[fieldMeta.type] ?? []) : [];

  return (
    <div className="grid grid-cols-[20px_minmax(0,1fr)_140px_minmax(0,1fr)_40px] items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
      <GripVertical className="h-4 w-4 text-gray-300" />

      <Select
        classNameDiv="w-full"
        value={rule.field}
        onChange={(event) => handleFieldChange(event.target.value)}
        options={fieldOptions}
        placeholder="Campo"
        className="h-10 py-2 text-sm"
      />

      <Select
        classNameDiv="w-full"
        value={rule.operator}
        onChange={(event) => handleOperatorChange(event.target.value as FilterOperator)}
        options={operatorOptions}
        className="h-10 py-2 text-sm"
      />

      {operatorIsNullStyle ? (
        <Select
          classNameDiv="w-full"
          value={rule.value || NULL_FILTER_VALUE}
          onChange={(event) => onRuleChange(rule.id, { value: event.target.value })}
          options={
            fieldMeta?.type === 'Boolean'
              ? [
                  { value: NULL_FILTER_VALUE, label: 'NULL' },
                  { value: 'true', label: 'true' },
                  { value: 'false', label: 'false' },
                ]
              : [{ value: NULL_FILTER_VALUE, label: 'NULL' }]
          }
          className="h-10 py-2 text-sm"
        />
      ) : enumOptions.length > 0 ? (
        <Select
          classNameDiv="w-full"
          value={rule.value}
          onChange={(event) => onRuleChange(rule.id, { value: event.target.value })}
          options={enumOptions.map((enumValue) => ({ value: enumValue, label: enumValue }))}
          placeholder="Value"
          className="h-10 py-2 text-sm"
        />
      ) : fieldMeta?.type === 'Boolean' ? (
        <Select
          classNameDiv="w-full"
          value={rule.value}
          onChange={(event) => onRuleChange(rule.id, { value: event.target.value })}
          options={[
            { value: 'true', label: 'true' },
            { value: 'false', label: 'false' },
          ]}
          placeholder="Value"
          className="h-10 py-2 text-sm"
        />
      ) : (
        <Input
          value={rule.value}
          onChange={(event) => onRuleChange(rule.id, { value: event.target.value })}
          placeholder={operatorNeedsListValue ? 'value1, value2' : 'value'}
          type={fieldMeta?.type === 'DateTime' ? 'datetime-local' : isNumericType(fieldMeta?.type ?? '') ? 'number' : 'text'}
          step={isNumericType(fieldMeta?.type ?? '') ? 'any' : undefined}
          className="h-10"
        />
      )}

      <Button
        size="sm"
        variant="ghost"
        className="h-10 w-10 p-0 text-gray-400 hover:bg-red-50 hover:text-red-600"
        onClick={() => onRemoveNode(rule.id)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
